import { Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import { useConstants } from "../../hooks/UseConstants";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Fetch from "../../services/Fetch";
import { useFinancialOperationsFilter } from "../../filter/UseFinancialOperationsFilter";
import { FormattedMessage } from "react-intl";
import AdminPageHero from "../../components/AdminPageHero";
import AdminFinanceNav from "../../components/AdminFinanceNav";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import PageContent from "../../components/PageContent";
import FinancialOperationsFilter from "../../popup/FinancialOperationsFilter";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { formatCurrency } from "../../helper/formatCurrency";
import ModalOverlay from "../../components/ModalOverlay";

function FinancialOperations() {
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { language, host } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const {
        from, setFrom,
        to, setTo,
        courseId, setCourseId,
        courseValue, setCourseValue,
        pathId, setPathId,
        pathValue, setPathValue,
        teacherId, setTeacherId,
        teacherValue, setTeacherValue,
    } = useFinancialOperationsFilter();
    const isAr = language === 'ar';
    const [loading, setLoading] = useState(true);
    const [filterWait, setFilterWait] = useState(false);
    const [search, setSearch] = useState('');
    const [order, setOrder] = useState('');
    const [entryType, setEntryType] = useState('');
    const [page, setPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [ledgerCount, setLedgerCount] = useState(0);
    const [cards, setCards] = useState({
        entries: { count: 0, amount: 0 },
        sales: { count: 0, amount: 0 },
        wallet_topups: { count: 0, amount: 0 },
        withdrawals: { count: 0, amount: 0 },
    });
    const [rows, setRows] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);

    const filterQueryString = useMemo(() => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (courseId) params.set('course_id', courseId);
        if (pathId) params.set('path_id', pathId);
        if (teacherId) params.set('teacher_id', teacherId);
        if (entryType) params.set('entry_type', entryType);
        if (order) {
            const [orderBy, direction] = order.split('|');
            if (orderBy) params.set('order_by', orderBy);
            if (direction) params.set('direction', direction);
        }
        return params.toString();
    }, [search, from, to, courseId, pathId, teacherId, entryType, order]);

    const ledgerQueryString = useMemo(() => {
        const params = new URLSearchParams(filterQueryString);
        params.set('page', String(page + 1));
        params.set('per_page', '12');
        return params.toString();
    }, [filterQueryString, page]);

    const loadLedger = async (showLoading = false) => {
        if (showLoading) setLoading(true);
        const result = await Fetch(`${host}/reports/ledger?${ledgerQueryString}`, 'GET');

        if (result.status === 200) {
            const payload = result.data?.data || {};
            const pagination = payload.pagination || {};
            setRows(Array.isArray(payload.items) ? payload.items : []);
            setCards(payload.cards || {
                entries: { count: 0, amount: 0 },
                sales: { count: 0, amount: 0 },
                wallet_topups: { count: 0, amount: 0 },
                withdrawals: { count: 0, amount: 0 },
            });
            setCurrentPage((pagination.current_page || 1) - 1);
            setTotalPages(pagination.last_page || 1);
            setLedgerCount(pagination.total || 0);
        } else {
            setRows([]);
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }

        setLoading(false);
        setFilterWait(false);
    };

    const exportLedger = async () => {
        const res = await fetch(`${host}/reports/ledger/export?${filterQueryString}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        if (!res.ok) {
            setSnackBar('error', isAr ? 'فشل تصدير الدفتر المالي' : 'Failed to export ledger');
            return;
        }

        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `financial-ledger-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
    };

    useEffect(() => {
        if (!wait) {
            loadLedger(true);
        }
    }, [wait, page, search, order, entryType, reloadKey]);

    const handleConfirmFilters = async () => {
        setPage(0);
        setPopup('filter', 'none');
        setReloadKey((value) => value + 1);
    };

    const getGroupLabel = (group) => {
        if (group === 'sale') return isAr ? 'بيع' : 'Sale';
        if (group === 'wallet_topup') return isAr ? 'شحن محفظة' : 'Wallet top-up';
        if (group === 'withdrawal') return isAr ? 'سحب' : 'Withdrawal';
        return isAr ? 'أخرى' : 'Other';
    };

    const getGroupColor = (group) => {
        if (group === 'sale') return 'success';
        if (group === 'wallet_topup') return 'info';
        if (group === 'withdrawal') return 'warning';
        return 'default';
    };

    const getTypeLabel = (row) => {
        if (row.entry_type_key === 'course_sale') return isAr ? 'شراء دورة' : 'Course sale';
        if (row.entry_type_key === 'path_sale') return isAr ? 'شراء مسار' : 'Path sale';
        if (row.entry_type_key === 'manual_topup') return isAr ? 'شحن يدوي' : 'Manual top-up';
        if (row.entry_type_key === 'qr_topup') return isAr ? 'شحن QR' : 'QR top-up';
        if (row.entry_type_key === 'teacher_withdrawal') return isAr ? 'سحب مدرس' : 'Teacher withdrawal';
        return row.entry_type_label;
    };

    const getStatusLabel = (row) => {
        if (row.status === 'approved') return isAr ? 'موافق عليه' : 'Approved';
        if (row.status === 'rejected') return isAr ? 'مرفوض' : 'Rejected';
        if (row.status === 'pending') return isAr ? 'قيد الانتظار' : 'Pending';
        return isAr ? 'مكتمل' : 'Completed';
    };

    return (
        <>
            <PageContent wait={wait} loading={loading && !wait}>
                <AdminPageHero />
                <AdminFinanceNav />

                <Box className="mb-3">
                    <Typography variant="h5" className="!font-bold flex items-center gap-2">
                        <ReceiptLongOutlinedIcon />
                        {isAr ? 'دفتر الأستاذ المالي' : 'Financial ledger'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" className="!mt-1">
                        {isAr ? 'جميع المبيعات وشحن المحافظ والسحوبات في شاشة تدقيق واحدة.' : 'Sales, wallet top-ups, and withdrawals in one audit screen.'}
                    </Typography>
                </Box>

                <Box className="flex flex-wrap gap-2 mb-4">
                        <DashboardStatCard
                            label={isAr ? 'كل الحركات' : 'All entries'}
                            value={`${cards.entries?.count ?? 0}`}
                            footer={formatCurrency(cards.entries?.amount ?? 0, isAr)}
                            className="flex-[1_1_22%] min-w-[170px] mx-0"
                        />
                        <DashboardStatCard
                            label={isAr ? 'إجمالي المبيعات' : 'Sales total'}
                            value={formatCurrency(cards.sales?.amount ?? 0, isAr)}
                            footer={`${cards.sales?.count ?? 0} ${isAr ? 'حركة' : 'entries'}`}
                            className="flex-[1_1_22%] min-w-[170px] mx-0"
                        />
                        <DashboardStatCard
                            label={isAr ? 'شحن المحافظ' : 'Wallet top-ups'}
                            value={formatCurrency(cards.wallet_topups?.amount ?? 0, isAr)}
                            footer={`${cards.wallet_topups?.count ?? 0} ${isAr ? 'حركة' : 'entries'}`}
                            className="flex-[1_1_22%] min-w-[170px] mx-0"
                        />
                        <DashboardStatCard
                            label={isAr ? 'السحوبات' : 'Withdrawals'}
                            value={formatCurrency(cards.withdrawals?.amount ?? 0, isAr)}
                            footer={`${cards.withdrawals?.count ?? 0} ${isAr ? 'حركة' : 'entries'}`}
                            className="flex-[1_1_22%] min-w-[170px] mx-0"
                        />
                    </Box>

                    <DashboardPanelCard className="mx-0 !mt-0">
                        <TableContainer
                            className="!rounded-b-xl"
                            component={Paper}
                            dir={isAr ? 'rtl' : 'ltr'}
                            sx={{ borderRadius: '0', boxShadow: 'none' }}
                        >
                            <Box className="min-h-12 py-2 px-2 flex justify-between items-center max-sm:flex-col gap-2">
                                <Box className="w-full flex items-center gap-2 max-sm:flex-col max-sm:items-stretch">
                                    <FilterAltOutlinedIcon className="cursor-pointer" onClick={() => setPopup('filter', 'flex')} fontSize="large" />
                                    <Box className="relative flex-1">
                                        <input
                                            style={{ backgroundColor: theme.palette.background.default }}
                                            onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                                            className="w-full h-12 rounded-md border indent-14 outline-none"
                                            placeholder={isAr ? 'ابحث بالاسم، المعاملة، الدورة، الملاحظات...' : 'Search by name, transaction, course, notes...'}
                                        />
                                        <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" sx={{ right: language === 'en' && '92%' }} />
                                    </Box>
                                </Box>
                                <Box className="flex items-center gap-2 max-sm:w-full max-sm:flex-wrap">
                                    <TextField
                                        select
                                        size="small"
                                        value={entryType}
                                        onChange={(e) => { setPage(0); setEntryType(e.target.value); }}
                                        sx={{ minWidth: 180 }}
                                    >
                                        <MenuItem value="">{isAr ? 'كل الأنواع' : 'All types'}</MenuItem>
                                        <MenuItem value="sale">{isAr ? 'كل المبيعات' : 'All sales'}</MenuItem>
                                        <MenuItem value="course_sale">{isAr ? 'مبيعات الدورات' : 'Course sales'}</MenuItem>
                                        <MenuItem value="path_sale">{isAr ? 'مبيعات المسارات' : 'Path sales'}</MenuItem>
                                        <MenuItem value="wallet_topup">{isAr ? 'كل الشحن' : 'All top-ups'}</MenuItem>
                                        <MenuItem value="manual_topup">{isAr ? 'شحن يدوي' : 'Manual top-up'}</MenuItem>
                                        <MenuItem value="qr_topup">{isAr ? 'شحن QR' : 'QR top-up'}</MenuItem>
                                        <MenuItem value="withdrawal">{isAr ? 'السحوبات' : 'Withdrawals'}</MenuItem>
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        value={order}
                                        onChange={(e) => { setPage(0); setOrder(e.target.value); }}
                                        sx={{ minWidth: 150 }}
                                    >
                                        <MenuItem value="">{isAr ? 'الأحدث' : 'Newest'}</MenuItem>
                                        <MenuItem value="amount|asc">{isAr ? 'المبلغ تصاعدي' : 'Amount asc'}</MenuItem>
                                        <MenuItem value="amount|desc">{isAr ? 'المبلغ تنازلي' : 'Amount desc'}</MenuItem>
                                        <MenuItem value="id|asc">{isAr ? 'المعرف تصاعدي' : 'ID asc'}</MenuItem>
                                    </TextField>
                                    <Button variant="outlined" size="small" onClick={exportLedger}>
                                        <FormattedMessage id="export" defaultMessage="Export Excel" />
                                    </Button>
                                    <Typography variant="body2" className="!text-gray-500">
                                        <FormattedMessage id="total_record" />: {ledgerCount}
                                    </Typography>
                                </Box>
                            </Box>

                            <Table sx={{ minWidth: 1200 }} aria-label="financial-ledger-table">
                                <TableHead className="bg-gray-200">
                                    <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_id" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'التصنيف' : 'Group'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_type" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'الحساب' : 'Account'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_details" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'المصدر/المنفذ' : 'Source / Processor'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="amount" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'حصة المنصة' : 'Platform share'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'حصة المدرس' : 'Teacher share'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}>{isAr ? 'الحالة' : 'Status'}</StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_date" /></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <StyledTableRow key={row.id} className="h-20">
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.id}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                <Chip size="small" color={getGroupColor(row.entry_group)} variant="outlined" label={getGroupLabel(row.entry_group)} />
                                            </StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{getTypeLabel(row)}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.account_name}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.teacher_name || '-'}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                <Typography variant="body2">{row.details}</Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.source || '-'}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{formatCurrency(row.amount, isAr)}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.platform_share != null ? formatCurrency(row.platform_share, isAr) : '-'}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.teacher_share != null ? formatCurrency(row.teacher_share, isAr) : '-'}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : row.status === 'pending' ? 'warning' : 'default'}
                                                    label={getStatusLabel(row)}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{row.date}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                    {!rows.length && (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={12} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 5 }}>
                                                    {isAr ? 'لا توجد حركات مطابقة للمرشحات الحالية.' : 'No ledger entries match the current filters.'}
                                                </Typography>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>

                            <Box className="flex justify-center items-center" dir="rtl">
                                <Button disabled={page + 1 === totalPages} className="cursor-pointer" onClick={() => setPage(currentPage + 1)}>
                                    <NavigateNextIcon fontSize="large" />
                                </Button>
                                <Typography variant="body1" className="!text-xl" dir='ltr'>{currentPage + 1} / {totalPages}</Typography>
                                <Button disabled={page + 1 === 1} className="cursor-pointer" onClick={() => setPage(currentPage - 1)}>
                                    <NavigateBeforeIcon fontSize="large" />
                                </Button>
                            </Box>
                        </TableContainer>
                    </DashboardPanelCard>

                <ModalOverlay id="filter" onClose={() => setPopup('filter', 'none')}>
                    <FinancialOperationsFilter
                        onClickClose={() => setPopup('filter', 'none')}
                        onClickConfirm={handleConfirmFilters}
                        filterWait={filterWait}
                        setFilterWait={setFilterWait}
                        from={from}
                        to={to}
                        courseValue={courseValue}
                        pathValue={pathValue}
                        setCourseId={setCourseId}
                        setCourseValue={setCourseValue}
                        setPathId={setPathId}
                        setFrom={setFrom}
                        setTo={setTo}
                        setPathValue={setPathValue}
                        setTeacherId={setTeacherId}
                        teacherValue={teacherValue}
                        setTeacherValue={setTeacherValue}
                    />
                </ModalOverlay>
            </PageContent>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default FinancialOperations;